import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatINR } from "@/lib/helpers";
import { CheckCircle, ShieldCheck } from "@phosphor-icons/react";

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const [rotate, setRotate] = useState({ current_password: "", new_password: "" });
  const [rotating, setRotating] = useState(false);

  const load = () => Promise.all([
    api.get("/admin/stats").then(r => setStats(r.data)).catch(()=>setStats({ error: true })),
    api.get("/admin/pricing").then(r => setPricing(r.data)).catch(()=>{}),
    api.get("/admin/organizations").then(r => setOrgs(r.data.items)).catch(()=>{}),
    api.get("/admin/readiness").then(r => setReadiness(r.data)).catch(()=>{}),
  ]);
  const [readiness, setReadiness] = useState(null);
  useEffect(() => { load(); }, []);

  if (stats?.error) return (
    <div className="card-base p-10 text-center max-w-lg mx-auto mt-8">
      <div className="w-14 h-14 rounded-full bg-secondary flex items-center justify-center mx-auto mb-4">
        <CheckCircle size={22} className="text-muted-foreground"/>
      </div>
      <h2 className="font-display font-bold text-2xl text-navy mb-2">Admin access required</h2>
      <p className="text-sm text-muted-foreground">You need superadmin privileges to view this page.</p>
    </div>
  );
  if (!stats || !pricing) return <div className="font-mono-data text-sm text-muted-foreground">Loading admin console…</div>;

  const savePricing = async () => {
    setSaving(true);
    try {
      // Only send simple config fields; packs saved via saveCreditPacks
      const { credit_packs, ...rest } = pricing;
      const r = await api.patch("/admin/pricing", rest);
      setPricing(r.data);
      toast.success("Pricing updated");
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };

  const saveCreditPacks = async () => {
    setSaving(true);
    try {
      const r = await api.patch("/admin/pricing", { credit_packs: pricing.credit_packs });
      setPricing(r.data);
      toast.success("Credit packs updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save packs");
    } finally { setSaving(false); }
  };

  const updatePack = (idx, patch) => {
    const packs = [...(pricing.credit_packs || [])];
    packs[idx] = { ...packs[idx], ...patch };
    setPricing({ ...pricing, credit_packs: packs });
  };

  const setOrgLimit = async (orgId, val) => {
    try {
      await api.patch(`/admin/organizations/${orgId}/ai-limit`, { ai_daily_limit: parseInt(val) || 0 });
      toast.success("AI limit updated");
      load();
    } catch { toast.error("Update failed"); }
  };

  const rotateAdmin = async () => {
    if (rotate.new_password.length < 8) return toast.error("Password must be at least 8 characters");
    setRotating(true);
    try {
      await api.post("/auth/rotate-password", rotate);
      toast.success("Superadmin password rotated");
      setRotate({ current_password: "", new_password: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Rotation failed"); }
    finally { setRotating(false); }
  };

  return (
    <div data-testid="admin-page" className="space-y-8 min-w-0">
      <div>
        <div className="eyebrow mb-1">Admin Console</div>
        <h1 className="font-display font-bold text-3xl lg:text-4xl tracking-tighter text-navy">Business overview</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {[["Users",stats.users],["Orgs",stats.organizations],["Products",stats.products],
          ["Platforms",stats.platforms_connected],["Paid",stats.paid_activations],["Revenue",formatINR(stats.revenue)]].map(([l,v])=>(
          <div key={l} className="kpi-card"><div className="kpi-label">{l}</div><div className="kpi-value">{v}</div></div>
        ))}
      </div>

      {/* Production Readiness */}
      {readiness && (
        <div className="card-base p-6" data-testid="production-readiness">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck size={18} className="text-orange"/>
            <div className="section-title">Production Readiness</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {[
              ["Hostinger", readiness.hostinger, ["ready"]],
              ["Database", readiness.database, ["ready"]],
              ["Admin Security", readiness.admin_security, ["ready"]],
              ["Razorpay", readiness.razorpay, ["live"]],
              ["Shopify", readiness.shopify, ["production","dev_store"]],
              ["AI Limits", readiness.ai_limits, ["configured"]],
              ["Email", readiness.email, ["configured"]],
              ["Background Jobs", readiness.background_jobs, ["running"]],
              ["SSL", readiness.ssl, ["configured"]],
              ["LLM Key", readiness.llm_key, ["configured"]],
            ].map(([label, val, good]) => {
              const ok = good.includes(val);
              const warn = ["dev_store","test"].includes(val);
              return (
                <div key={label} className="border border-border rounded-lg p-3 min-w-0">
                  <div className="eyebrow truncate">{label}</div>
                  <div className={`mt-1 pill ${ok?"pill-live":warn?"pill-draft":"pill-missing"}`}>{String(val).replace(/_/g," ")}</div>
                </div>
              );
            })}
          </div>
          <div className="text-xs text-muted-foreground mt-4 font-mono-data">
            APP_URL: {readiness.app_url || "—"} · ALLOW_DEV_ADMIN: {String(readiness.allow_dev_admin)} · dev admin exists: {String(readiness.dev_admin_exists)}
          </div>
        </div>
      )}

      {/* Superadmin password rotation */}
      <div className="card-base p-6 border-2" style={{borderColor:"hsl(var(--ecom-orange))"}}>
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck size={18} className="text-orange"/>
          <div className="section-title">Rotate Superadmin Password</div>
        </div>
        <p className="text-sm text-muted-foreground mb-4">Never leave the default <code>admin1234</code> active in production.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl">
          <input data-testid="rotate-current" type="password" placeholder="Current password" value={rotate.current_password} onChange={(e)=>setRotate({...rotate, current_password: e.target.value})} className="input-base"/>
          <input data-testid="rotate-new" type="password" placeholder="New password (min 8)" value={rotate.new_password} onChange={(e)=>setRotate({...rotate, new_password: e.target.value})} className="input-base"/>
        </div>
        <button data-testid="rotate-submit" disabled={rotating} onClick={rotateAdmin} className="btn-primary mt-4">
          {rotating ? "Rotating…" : "Rotate password"}
        </button>
      </div>

      {/* Pricing */}
      <div className="card-base p-6">
        <div className="section-title mb-4">Pricing Configuration</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[["price_per_day","Price per day (₹)"],["free_platforms","Free platforms"],["trial_price_7d","7-day trial (₹)"],
            ["monthly_price","Monthly (₹)"],["yearly_price","Yearly (₹)"],["currency","Currency"],
            ["free_credits","Free credits on signup"],["max_marketplaces_per_run","Max marketplaces / run"],
            ["max_products_per_run","Max products / run"]].map(([k,label])=>(
            <div key={k}><label className="eyebrow block mb-1.5">{label}</label>
              <input data-testid={`price-${k}`} value={pricing[k] ?? ""} onChange={(e)=>setPricing({...pricing, [k]: k==="currency"?e.target.value:parseFloat(e.target.value)||0})} className="input-base"/>
            </div>
          ))}
        </div>
        <button data-testid="save-pricing" disabled={saving} onClick={savePricing} className="btn-primary mt-4">{saving?"Saving…":"Save Pricing"}</button>
      </div>

      {/* Credit Packs */}
      <div className="card-base p-6" data-testid="credit-packs-editor">
        <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
          <div>
            <div className="section-title">Gap Finder Credit Packs</div>
            <div className="text-xs text-muted-foreground mt-1">
              Sold via Razorpay. Server verifies signature and writes to the immutable credit ledger.
            </div>
          </div>
          <button
            data-testid="add-credit-pack"
            onClick={() => setPricing({
              ...pricing,
              credit_packs: [...(pricing.credit_packs || []), { id: `p${Date.now()}`, price: 99, credits: 100, active: true, order: (pricing.credit_packs || []).length + 1, label: "New Pack" }]
            })}
            className="btn-secondary py-1.5 px-3 text-[11px]"
          >
            + Add Pack
          </button>
        </div>
        <div className="scroll-x">
          <table className="table-base">
            <thead><tr><th>ID</th><th>Label</th><th>Price (₹)</th><th>Credits</th><th>Order</th><th>Active</th><th></th></tr></thead>
            <tbody>
              {(pricing.credit_packs || []).map((pk, idx) => (
                <tr key={pk.id} data-testid={`credit-pack-row-${pk.id}`}>
                  <td className="font-mono-data text-xs">{pk.id}</td>
                  <td>
                    <input className="input-base py-1 px-2 text-xs w-32" value={pk.label || ""} onChange={(e) => updatePack(idx, { label: e.target.value })} />
                  </td>
                  <td>
                    <input type="number" className="input-base py-1 px-2 text-xs w-24 tabular-nums" value={pk.price} onChange={(e) => updatePack(idx, { price: parseFloat(e.target.value) || 0 })} />
                  </td>
                  <td>
                    <input type="number" className="input-base py-1 px-2 text-xs w-24 tabular-nums" value={pk.credits} onChange={(e) => updatePack(idx, { credits: parseInt(e.target.value) || 0 })} />
                  </td>
                  <td>
                    <input type="number" className="input-base py-1 px-2 text-xs w-16 tabular-nums" value={pk.order || 0} onChange={(e) => updatePack(idx, { order: parseInt(e.target.value) || 0 })} />
                  </td>
                  <td>
                    <input type="checkbox" checked={pk.active !== false} onChange={(e) => updatePack(idx, { active: e.target.checked })} />
                  </td>
                  <td>
                    <button data-testid={`delete-pack-${pk.id}`} onClick={() => setPricing({ ...pricing, credit_packs: pricing.credit_packs.filter((_, i) => i !== idx) })} className="text-red-600 hover:underline text-xs">Remove</button>
                  </td>
                </tr>
              ))}
              {(pricing.credit_packs || []).length === 0 && (
                <tr><td colSpan={7} className="text-center text-muted-foreground py-6">No packs configured.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <button data-testid="save-credit-packs" disabled={saving} onClick={saveCreditPacks} className="btn-primary mt-4">
          {saving ? "Saving…" : "Save Credit Packs"}
        </button>
      </div>

      {/* Organizations + AI limits */}
      <div className="card-base overflow-hidden">
        <div className="p-4 border-b border-border section-title">Organizations · AI Limits</div>
        <div className="scroll-x">
          <table className="table-base">
            <thead><tr><th>Organization</th><th>Users</th><th>Products</th><th>Platforms</th><th>AI Used Today</th><th>Daily Limit</th></tr></thead>
            <tbody>
              {orgs.map(o => (
                <tr key={o.id}>
                  <td className="font-medium text-navy">{o.name}</td>
                  <td className="font-mono-data">{o.users}</td>
                  <td className="font-mono-data">{o.products}</td>
                  <td className="font-mono-data">{o.platforms}</td>
                  <td className="font-mono-data">{o.ai_used_today}</td>
                  <td>
                    <input type="number" defaultValue={o.ai_daily_limit}
                      onBlur={(e) => setOrgLimit(o.id, e.target.value)}
                      data-testid={`ai-limit-${o.id}`}
                      className="input-base py-1 px-2 text-xs w-24 tabular-nums"/>
                  </td>
                </tr>
              ))}
              {orgs.length === 0 && <tr><td colSpan={6} className="text-center text-muted-foreground py-6">No orgs yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Payments */}
      <div className="card-base overflow-hidden">
        <div className="p-4 border-b border-border section-title">Recent Payments</div>
        <div className="scroll-x">
          <table className="table-base">
            <thead><tr><th>Date</th><th>Platform</th><th>Days</th><th className="text-right">Amount</th><th>Status</th><th>Mode</th></tr></thead>
            <tbody>
              {[...stats.payments].sort((a,b)=>new Date(b.created_at)-new Date(a.created_at)).map(p => (
                <tr key={p.id}>
                  <td className="font-mono-data text-xs">{new Date(p.created_at).toLocaleString()}</td>
                  <td className="capitalize">{p.platform}</td>
                  <td className="font-mono-data">{p.days}</td>
                  <td className="text-right font-mono-data tabular-nums">{formatINR(p.amount)}</td>
                  <td><span className={`pill ${p.status === "paid_live" ? "pill-live" : "pill-draft"}`}>{p.status}</span></td>
                  <td className="font-mono-data text-xs uppercase text-muted-foreground">{p.mode}</td>
                </tr>
              ))}
              {stats.payments.length === 0 && <tr><td colSpan={6} className="text-center text-muted-foreground py-6">No payments yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
